import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from '../hooks/useTranslation';
import PlatformButton from './PlatformButton';
import { isNative, isIOS, isAndroid, getPlatformType } from '../utils/platformUtils';
import './LanguageSwitcher.css';

const LanguageSwitcher = ({ 
  variant = 'dropdown', // 'dropdown' or 'toggle'
  showFlags = true,
  showNativeNames = true,
  className = '',
  size = 'medium',
  position = 'auto' // 'auto', 'top-right', 'top-left', 'bottom-right', 'bottom-left'
}) => {
  const { 
    getCurrentLanguage, 
    changeLanguage, 
    getAvailableLanguages
  } = useTranslation();
  
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef(null);
  const currentLanguage = getCurrentLanguage();
  const availableLanguages = getAvailableLanguages();
  
  // Platform detection
  const platformType = getPlatformType();
  const isNativePlatform = isNative();
  const isIOSPlatform = isIOS();
  const isAndroidPlatform = isAndroid();
  
  // Generate platform-specific CSS classes
  const getPlatformClasses = () => {
    const baseClasses = ['language-switcher'];
    
    // Platform-specific classes
    if (isNativePlatform) {
      baseClasses.push('platform-native');
      if (isIOSPlatform) {
        baseClasses.push('platform-ios');
      } else if (isAndroidPlatform) {
        baseClasses.push('platform-android');
      }
    } else {
      baseClasses.push('platform-web');
      if (platformType === 'ios-pwa') {
        baseClasses.push('platform-ios-pwa');
      } else if (platformType === 'android-pwa') {
        baseClasses.push('platform-android-pwa');
      }
    }
    
    // Position classes
    if (position !== 'auto') {
      baseClasses.push(`position-${position}`);
    }
    
    // Variant classes
    baseClasses.push(`variant-${variant}`);
    
    // Size classes
    baseClasses.push(`size-${size}`);
    
    return baseClasses.join(' ');
  };
  

  const handleLanguageChange = (languageCode) => {
    changeLanguage(languageCode);
    setIsOpen(false);
  };

  const updateDropdownPosition = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  };

  const handleToggle = () => {
    if (!isOpen) {
      updateDropdownPosition();
    }
    setIsOpen(!isOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is on trigger button
      const isTriggerClick = triggerRef.current && triggerRef.current.contains(event.target);
      
      // Check if click is on dropdown portal (rendered in document.body)
      const isDropdownClick = event.target.closest('.language-dropdown-portal');
      
      if (!isTriggerClick && !isDropdownClick) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      // Add a small delay to prevent immediate closure
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);
      
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  // Update position on scroll/resize
  useEffect(() => {
    if (isOpen) {
      const handleScroll = () => updateDropdownPosition();
      const handleResize = () => updateDropdownPosition();
      
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
      
      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [isOpen]);

  const getFlagEmoji = (languageCode) => {
    const flags = {
      en: '🇺🇸',
      hi: '🇮🇳'
    };
    return flags[languageCode] || '🌐';
  };

  const getLanguageName = (language) => {
    if (showNativeNames && language.nativeName) {
      return language.nativeName;
    }
    return language.name || language.code;
  };

  if (variant === 'toggle') {
    // Toggle between English and Hindi
    const nextLanguage = currentLanguage === 'en' ? 'hi' : 'en';
    const nextLanguageData = availableLanguages.find(lang => lang.code === nextLanguage);
    
    return (
      <div className={`${getPlatformClasses()} ${className}`}>
        <PlatformButton
          onClick={() => handleLanguageChange(nextLanguage)}
          className="language-toggle"
          size={size}
          variant="outline"
        >
          {showFlags && <span className="language-flag">{getFlagEmoji(nextLanguage)}</span>}
          <span className="language-name">{getLanguageName(nextLanguageData)}</span>
        </PlatformButton>
      </div>
    );
  }

  // Dropdown variant
  const DropdownPortal = () => {
    if (!isOpen) return null;

    return createPortal(
      <div 
        className="language-dropdown-portal"
        style={{
          position: 'absolute',
          top: dropdownPosition.top,
          left: dropdownPosition.left,
          width: dropdownPosition.width,
          zIndex: 10000
        }}
      >
        <div 
          className="language-dropdown"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {availableLanguages.map((language) => (
            <button
              key={language.code}
              className={`language-option ${currentLanguage === language.code ? 'active' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                handleLanguageChange(language.code);
              }}
            >
              {showFlags && <span className="language-flag">{getFlagEmoji(language.code)}</span>}
              <span className="language-name">{getLanguageName(language)}</span>
              {currentLanguage === language.code && (
                <span className="language-check">✓</span>
              )}
            </button>
          ))}
        </div>
      </div>,
      document.body
    );
  };

  return (
    <div className={`${getPlatformClasses()} ${className} ${isOpen ? 'open' : ''}`}>
      <button
        ref={triggerRef}
        onClick={handleToggle}
        className="language-trigger"
        type="button"
      >
        {showFlags && <span className="language-flag">{getFlagEmoji(currentLanguage)}</span>}
        <span className="language-name">
          {getLanguageName(availableLanguages.find(lang => lang.code === currentLanguage))}
        </span>
        <span className="language-arrow">{isOpen ? '▲' : '▼'}</span>
      </button>

      <DropdownPortal />
    </div>
  );
};

export default LanguageSwitcher;
